<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
        <b-button
          class="left-right"
          type="is-success"
          icon-left="plus"
          :disabled="selectedPlant === null"
          @click="showCreateForm()"
        >
          Create New Movement
        </b-button>
      </div>
      <div class="columns">
        <div class="column">
          <b-field grouped>
            <b-field label="Start Date">
              <b-datepicker
                v-model="start"
                name="startdate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="Start date"
              />
            </b-field>
            <b-field label="End Date">
              <b-datepicker
                v-model="end"
                name="enddate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="End date"
              />
            </b-field>
            <b-field v-if="hasGroupPlants" label="Plant">
              <b-select v-model="selectedPlant" icon="industry" expanded>
                <option v-for="plant in groupPlants" :key="plant.id" :value="plant">
                  {{ plant.name }}
                </option>
              </b-select>
            </b-field>
            <b-field v-else label="Plant">
              <multiselect
                v-model="selectedPlant"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
          </b-field>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || selectedPlant === null"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
          </b-field>
          <ag-grid-vue
            v-if="movements.length != 0"
            style="width: 100%; height: 700px;"
            class="ag-theme-balham"
            :row-data="movements"
            :grid-options="gridOptions"
            :pagination="true"
            :pagination-auto-page-size="true"
            :cache-overflow-size="0"
            :multi-sort-key="'ctrl'"
            :modules="modules"
            :get-context-menu-items="getContextMenuItems"
          />
        </div>
      </div>
    </div>
    <b-modal :can-cancel="['escape', 'x']" :active.sync="isCreatingMovement" scroll="keep" has-modal-card>
      <MovementForm :plant="selectedPlant" @submit="submitMovement" />
    </b-modal>
    <b-modal :active.sync="isEditingMovement" scroll="keep" has-modal-card>
      <form v-if="isEditingMovement" @submit.prevent="submitEdit">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit Movement
              <span v-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Crate Type">
                <b-select v-model="editableMovement.type" placeholder="Crate Type">
                  <option v-for="type in crateTypeOptions" :key="type.value" :value="type.value">
                    {{ type.name }}
                  </option>
                </b-select>
              </b-field>
              <b-field label="Date">
                <b-datepicker
                  v-model="editableMovement.date"
                  name="date"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="Date"
                />
              </b-field>
              <b-field label="Shift">
                <b-select v-model="editableMovement.shift" placeholder="Shift">
                  <option value="AM">
                    AM
                  </option>
                  <option value="PM">
                    PM
                  </option>
                  <option value="Night">
                    Night
                  </option>
                </b-select>
              </b-field>
            </b-field>
            <b-field grouped>
              <b-field label="Number of Crates">
                <b-input v-model="editableMovement.num_crates" type="number" placeholder="Crates" />
              </b-field>
            </b-field>
            <b-field grouped>
              <b-field label="Hours">
                <b-input v-model="editableMovement.hours" type="number" step="0.25" placeholder="Hours" />
              </b-field>
            </b-field>
            <b-field label="Comment">
              <b-input v-model="editableMovement.comment" type="text" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { AllModules } from '@ag-grid-enterprise/all-modules'
import CrateMovements from '../../services/v3/crateMovements'
import { mapState } from 'vuex'
import MovementForm from './movementForm.vue'
import plantSelector from '@/mixins/plantSelector'
import { restoreConsultingPlant } from '@/helpers/plants'
import ClientCrateType from '@/services/v3/clientCrateType'
import { UNIVERSAL_CRATE_TYPES, LEGACY_CLIENT_TYPES, VIRTUAL_TYPE_MEMBERS } from '@/helpers/crateTypes'
// import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'

export default {
  name: 'CrateMovements',
  components: {
    MovementForm
  },
  mixins: [plantSelector],
  data: function () {
    return {
      editableMovement: null,
      start: null,
      end: new Date(),
      selectedPlant: restoreConsultingPlant(),
      isLoading: false,
      isCreatingMovement: false,
      isEditingMovement: false,
      movements: [],
      clientCrateTypes: [],
      modules: AllModules,
      gridOptions: {
        cacheBlockSize: 20,
        paginationPageSize: 20,
        enableRangeSelection: true,
        defaultColDef: {
          floatingFilter: true,
          enableValue: true,
          enableRowGroup: true,
          enablePivot: false,
          resizable: true
        },
        columnDefs: [
          { headerName: 'Operation',
            field: 'operation',
            filter: 'agTextColumnFilter',
            width: 180,
            sortable: true,
            filterParams: { suppressAndOrCondition: true },
            valueGetter: (params) => {
              return params.data && params.data.operation ? params.data.operation.replace('washing', 'manual washing') : ''
            }
          },
          { headerName: 'Crates',
            field: 'num_crates',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Crate Type',
            field: 'type',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Comment',
            field: 'comment',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Hours',
            field: 'hours',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            sort: 'Desc',
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Date',
            field: 'date',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            sort: 'Desc',
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Shift',
            field: 'shift',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          }
        ],
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                // tool panel columns won't move when columns are reordered in the grid
                suppressSyncLayoutWithGrid: true,
                // prevents columns being reordered from the Columns Tool Panel
                suppressColumnMove: true
              }
            }
          ],
          defaultToolPanel: ''
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    // '5.X' is a reporting pool the forecast expands away, so a movement must never
    // be rebound to it.
    crateTypeOptions () {
      return [
        ...UNIVERSAL_CRATE_TYPES.filter(t => !VIRTUAL_TYPE_MEMBERS[t.value]),
        ...LEGACY_CLIENT_TYPES,
        ...this.clientCrateTypes.map(t => ({ name: t.name, value: t.name }))
      ]
    }
  },
  mounted: async function () {
    this.start = new Date()
    this.start.setDate(new Date().getDate() - 1)
    await this.loadPlants()
  },
  methods: {
    resetData () {
      this.movements = []
    },
    showCreateForm () {
      this.isCreatingMovement = true
    },
    showEditForm (movement) {
      this.isEditingMovement = true
      this.editableMovement = movement
      this.editableMovement.date = new Date(new Date(this.editableMovement.date).toISOString().split('T')[0] + 'T00:00:00')
    },
    async getData () {
      if (!this.selectedPlant || !this.selectedPlant.id) {
        this.$buefy.toast.open({ message: 'Please select a plant', duration: 3000, type: 'is-danger' })
        return
      }
      sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      this.isLoading = true
      try {
        // Without the plant's client-specific types in the edit dropdown, opening and
        // saving one of their movements would silently rebind it to another type.
        this.clientCrateTypes = await ClientCrateType.getForSite(this.selectedPlant.id)
        const data = await CrateMovements.getTable({
          start: this.start.toLocaleDateString('en-CA'),
          end: this.end.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id
        })
        data.forEach(element => {
          this.movements.push({
            id: element.id,
            id_site: element.id_site,
            operation: element.operation,
            num_crates: element.num_crates,
            status_crate_end: element.status_crate_end,
            status_crate_start: element.status_crate_start,
            type: element.status_crate_end.split('__')[0],
            shift: element.shift,
            hours: element.hours,
            date: new Date(element.date.split('Z')[0]).toLocaleDateString('en-CA'),
            comment: element.comment
          })
        })
        if (this.movements.length > 0) {
          this.$buefy.toast.open({ message: 'Data loaded', duration: 3000, type: 'is-success' })
        } else {
          this.$buefy.toast.open({ message: 'No corresponding movements found', duration: 3000, type: 'is-danger' })
        }
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
        this.isLoading = false
      }
      this.isLoading = false
    },
    getContextMenuItems (params) {
      const selectedMovement = params.node.data
      let customMenu = []
      const genericMenu = [
        'separator',
        'copy',
        'copyWithHeaders',
        'separator',
        'export'
      ]
      customMenu.push({
        name: 'Edit Movement',
        action: () => {
          this.showEditForm(selectedMovement)
        },
        icon: '<i class="fas fa-fw fa-edit" ></i>'
      })
      customMenu.push({
        name: 'Delete Movement',
        action: () => {
          this.deleteMovement(selectedMovement)
        },
        icon: '<i class="fas fa-fw fa-xmark" ></i>'
      })
      const result = [...customMenu, ...genericMenu]
      return result
    },
    submitMovement (movement) {
      this.isCreatingMovement = false
      try {
        movement.forEach((element) => {
          CrateMovements.createMovement(element)
        })
        this.$buefy.toast.open({ message: 'Movement(s) created', duration: 3000, type: 'is-success' })
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async submitEdit () {
      this.isEditingMovement = false
      // IMPORTANT TO MAKE EXCEPTIONS IF MOVEMENT TYPE CHANGES CRATE TYPE
      if (this.editableMovement.status_crate_start.split('__')[0] !== this.editableMovement.status_crate_end.split('__')[0]) {
        this.$buefy.toast.open({ message: 'movement crate type cant be changed', duration: 3000, type: 'is-danger' })
      } else {
        this.editableMovement.status_crate_start = this.editableMovement.type + '__' + this.editableMovement.status_crate_start.split('__')[1]
        this.editableMovement.status_crate_end = this.editableMovement.type + '__' + this.editableMovement.status_crate_end.split('__')[1]
      }
      this.editableMovement.date = this.editableMovement.date.toLocaleDateString('en-CA')
      try {
        await CrateMovements.updateMovement(this.editableMovement)
        this.$buefy.toast.open({ message: 'Movement updated', duration: 3000, type: 'is-success' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async deleteMovement (movement) {
      try {
        await CrateMovements.archiveMovement(movement.id)
        this.$buefy.toast.open({ message: 'Movement Deleted', duration: 3000, type: 'is-warning' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    }
  }
}

</script>
